import React, { useState, useEffect, useMemo } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Mail, Phone, ChevronDown } from "lucide-react";
import { PromoBanner } from "@/components/PromoBanner";

const normalizeSocial = (raw, base) => {
  if (!raw) return "";
  const v = String(raw).trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "").replace(/^\/+/, "");
  return `${base}${handle}`;
};
export const toInstaUrl = (v) => normalizeSocial(v, "https://instagram.com/");
export const toFbUrl = (v) => normalizeSocial(v, "https://facebook.com/");
import { api } from "@/lib/auth";

const NAV = [
  { to: "/", label: "Home" },
  { label: "About", children: [
    { to: "/about", label: "About Us" },
    { to: "/team", label: "Meet the Team" },
    { to: "/locations", label: "Locations" },
  ]},
  { label: "Our Services", children: [
    { to: "/classes", label: "Classes" },
    { to: "/competition-team", label: "Competition Team" },
    { to: "/workshops", label: "Workshops" },
    { to: "/events", label: "Events" },
    { to: "/hire", label: "Hire Us" },
  ]},
  { to: "/timetable", label: "Timetable" },
  { to: "/achievements", label: "Accolades" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

function NavDropdown({ label, items }) {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const isActive = items.some(i => loc.pathname === i.to);
  const slug = label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`link-underline uppercase tracking-wider text-xs font-semibold whitespace-nowrap flex items-center gap-1 ${isActive ? "text-white active" : "text-zinc-400 hover:text-white"}`}
        data-testid={`nav-${slug}`}
        aria-expanded={open}
      >
        {label} <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-3 z-50" data-testid={`nav-${slug}-menu`}>
          <div className="min-w-[220px] bg-black border border-white/10 py-2 shadow-2xl">
            {items.map(i => (
              <NavLink
                key={i.to}
                to={i.to}
                onClick={() => setOpen(false)}
                className={({isActive}) => `block px-4 py-2 uppercase tracking-wider text-xs font-semibold ${isActive ? "text-red-500 bg-white/5" : "text-zinc-300 hover:text-white hover:bg-white/5"}`}
                data-testid={`nav-${slug}-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {i.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(to).getTime();
  if (!target || isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const label = days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  return <span className="font-bold uppercase tracking-widest text-xs bg-black/30 px-2 py-1 rounded" data-testid="announcement-countdown">Ends in {label}</span>;
}

export default function Site() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(null);
  const settings = useMemo(() => loaded || {}, [loaded]);
  const loc = useLocation();
  const outletCtx = useMemo(() => ({ settings }), [settings]);

  useEffect(() => { api.get("/settings").then(r => setLoaded(r.data || {})).catch(() => setLoaded(l => l || {})); }, [loc.pathname]);
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  useEffect(() => {
    if (settings.site_name) document.title = `${settings.site_name}${settings.tagline ? " — " + settings.tagline : ""}`;
    if (settings.seo_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = settings.seo_description;
    }
    if (settings.favicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = settings.favicon;
    }
    // Live brand color
    if (settings.primary_color) {
      document.documentElement.style.setProperty("--red", settings.primary_color);
      document.documentElement.style.setProperty("--brand", settings.primary_color);
    }
  }, [settings]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Announcement banner */}
      <PromoBanner />
      {settings.announcement_active && settings.announcement_text && (
        <div className="bg-red-600 text-white text-sm text-center py-2 px-4 flex flex-wrap items-center justify-center gap-3" data-testid="announcement-banner">
          <span>{settings.announcement_text}</span>
          {settings.announcement_ends_at && <Countdown to={settings.announcement_ends_at} />}
          {settings.announcement_link_url && (
            <Link to={settings.announcement_link_url} className="underline font-bold uppercase tracking-widest text-xs">{settings.announcement_link_label || "Learn more"}</Link>
          )}
        </div>
      )}
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
          <Link to="/" data-testid="site-logo" className="flex items-center gap-2 sm:gap-3 group min-w-0">
            {settings.logo_light && (
              <img src={settings.logo_light} alt="" className="h-9 sm:h-10 md:h-12 w-auto object-contain shrink-0"/>
            )}
            <div className="font-display text-base sm:text-lg tracking-wider whitespace-nowrap truncate">{settings.site_name || "2RIGHTFEET DANCE SCHOOL"}</div>
          </Link>
          <nav className="hidden xl:flex items-center gap-5 text-sm mx-6">
            {NAV.map(n => n.children ? (
              <NavDropdown key={n.label} label={n.label} items={n.children} />
            ) : (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
                className={({isActive}) => `link-underline uppercase tracking-wider text-xs font-semibold whitespace-nowrap ${isActive ? "text-white active" : "text-zinc-400 hover:text-white"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="hidden md:block"><Link to="/classes" className="btn-red whitespace-nowrap" data-testid="cta-book-nav">Book a class</Link></div>
            <Link to="/classes" className="md:hidden bg-red-600 text-white text-[11px] font-bold uppercase tracking-widest px-3 py-2" data-testid="cta-book-nav-mobile">Book</Link>
            <button className="xl:hidden p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="xl:hidden border-t border-white/10 bg-black max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
              {NAV.map(n => n.children ? (
                <div key={n.label} className="py-1">
                  <div className="uppercase tracking-wider text-[10px] font-bold text-red-500 mt-2 mb-1" data-testid={`mobile-group-${n.label.toLowerCase().replace(/\s+/g,"-")}`}>{n.label}</div>
                  {n.children.map(c => (
                    <NavLink key={c.to} to={c.to} onClick={() => setOpen(false)} className={({isActive}) =>
                      `pl-3 py-1.5 block uppercase tracking-wider text-sm font-semibold ${isActive ? "text-red-500" : "text-zinc-300"}`}>
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} className={({isActive}) =>
                  `py-2 uppercase tracking-wider text-sm font-semibold ${isActive ? "text-red-500" : "text-zinc-300"}`}>
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{loaded ? <Outlet context={outletCtx} /> : <div className="min-h-screen bg-black" data-testid="site-loading" />}</main>

      {/* Footer */}
      <footer className="mt-20 border-t border-white/10 bg-[#050505]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              {settings.logo_light
                ? <img src={settings.logo_light} alt={settings.site_name || "2RightFeet"} className="h-12 w-auto object-contain" data-testid="footer-logo" />
                : <div className="w-9 h-9 bg-red-600 grid place-items-center font-display">2R</div>}
              <div className="font-display text-xl">{settings.site_name || "2RIGHTFEET"}</div>
            </div>
            <p className="text-zinc-400 text-sm">{settings.footer_note || "Family-run since 2019 · Melbourne"}</p>
            <div className="flex gap-3 mt-4">
              {settings.instagram && <a href={toInstaUrl(settings.instagram)} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="p-2 border border-white/10 hover:border-red-500"><Instagram size={16} /></a>}
              {settings.facebook && <a href={toFbUrl(settings.facebook)} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="p-2 border border-white/10 hover:border-red-500"><Facebook size={16} /></a>}
            </div>
          </div>
          <div>
            <h4 className="eyebrow mb-4">Contact</h4>
            <ul className="space-y-2 text-sm text-zinc-400">
              {settings.contact_email && <li className="flex items-center gap-2"><Mail size={14} />{settings.contact_email}</li>}
              {settings.contact_phone && <li className="flex items-center gap-2"><Phone size={14} />{settings.contact_phone}</li>}
              <li><Link to="/p/privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/p/terms" className="hover:text-white">Terms & Conditions</Link></li>
              <li><Link to="/p/refund-policy" className="hover:text-white">Refund Policy</Link></li>
              <li><Link to="/admin" className="text-zinc-600 hover:text-red-500 text-xs">Admin</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-6 text-center text-xs text-zinc-600">
          © {new Date().getFullYear()} {settings.site_name || "2RightFeet Dance School"} · Made with rhythm.
        </div>
      </footer>
    </div>
  );
}
